import { formatDistanceToNow } from "date-fns";
import { type Ref, useId, useLayoutEffect, useRef } from "react";

import {
    MarkdownEditor,
    type EditorHeading,
    type MarkdownEditorHandle,
} from "@/components/shared/MarkdownEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type SkillEditorSaveStatus = "saved" | "unsaved" | "new";

export interface SkillEditorInboundLink {
    clientKey: string;
    name: string;
}

interface SkillEditorPaneBaseProps {
    clientKey: string;
    resetEpoch: number;
    content: string;
    onContentChange: (content: string) => void;
    contentError?: string | null;
    status: SkillEditorSaveStatus;
    savedAt?: string | null;
    suggestions?: readonly string[];
    editorRef?: Ref<MarkdownEditorHandle>;
    brokenTargets?: string[];
    onJump?: (typedTarget: string) => void;
    inboundLinks?: SkillEditorInboundLink[];
    onSelectSource?: (clientKey: string) => void;
    recognitionVocabulary?: readonly string[];
    onHeadingsChange?: (headings: readonly EditorHeading[]) => void;
    isSaving?: boolean;
}

export interface SkillEditorPaneIndexProps extends SkillEditorPaneBaseProps {
    kind: "index";
    description: string;
    onDescriptionChange: (description: string) => void;
    descriptionError?: string | null;
}

export interface SkillEditorPaneReferenceProps extends SkillEditorPaneBaseProps {
    kind: "reference";
    name: string;
    onNameChange: (name: string) => void;
    onNameCommit: () => void;
    onNameCancel: (name: string) => void;
    renameOrigin?: string | null;
    nameError?: string | null;
    onDelete: () => void;
}

export type SkillEditorPaneProps =
    | SkillEditorPaneIndexProps
    | SkillEditorPaneReferenceProps;

const DESCRIPTION_MAX_LENGTH = 1024;

// Matches the backend name column; longer names also overflow the recursive
// mention recognizer before validation can report them.
const NAME_MAX_LENGTH = 255;

const SAVE_STATUS: Record<
    SkillEditorSaveStatus,
    { label: string; className: string }
> = {
    saved: { label: "Saved", className: "" },
    unsaved: { label: "Unsaved changes", className: "text-brand-copper" },
    new: { label: "New", className: "text-brand-copper" },
};

const CONTENT_COLUMN_CLASS = "mx-auto w-full max-w-[46rem] px-8";

const FOOTER_LINK_CLASS =
    "focus-visible:ring-ring/50 shrink-0 rounded-sm underline-offset-2 outline-none hover:underline focus-visible:ring-2";

const countWords = (content: string) => {
    const trimmed = content.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
};

export function SkillEditorPane(props: SkillEditorPaneProps) {
    const {
        clientKey,
        resetEpoch,
        content,
        onContentChange,
        contentError,
        status,
        savedAt,
        suggestions = [],
        editorRef,
        brokenTargets = [],
        onJump,
        inboundLinks = [],
        onSelectSource,
        recognitionVocabulary,
        onHeadingsChange,
        isSaving = false,
    } = props;
    const scrollRegion = useRef<HTMLDivElement>(null);
    const sessionStartName = useRef("");
    const fieldId = useId();

    // The scroll offset belongs to the document the editor just replaced, so it
    // is meaningless the moment the editor's key changes.
    useLayoutEffect(() => {
        const region = scrollRegion.current;
        if (region) region.scrollTop = 0;
    }, [clientKey, resetEpoch]);

    const referenceName = props.kind === "reference" ? props.name.trim() : "";
    const wordCount = countWords(content);
    const saveStatus = SAVE_STATUS[status];
    const statusLabel =
        status === "saved" && savedAt
            ? `Saved ${formatDistanceToNow(new Date(savedAt), { addSuffix: true })}`
            : saveStatus.label;

    return (
        <section
            data-testid="skill-editor-pane"
            className="flex h-full min-h-0 flex-col"
        >
            <div
                ref={scrollRegion}
                data-testid="skill-editor-scroll"
                className="min-h-0 flex-1 overflow-y-auto"
            >
                <div
                    data-testid="skill-editor-column"
                    className={cn(CONTENT_COLUMN_CLASS, "py-8")}
                >
                    {props.kind === "index" ? (
                        <h2
                            id={`${fieldId}-title`}
                            className="font-heading text-2xl font-semibold tracking-tight"
                        >
                            Instructions
                        </h2>
                    ) : (
                        // A focused input that becomes disabled fires blur, and
                        // blur commits the rename, so a pending save makes the
                        // field read-only and guards the commit handlers instead.
                        <Input
                            id={`${fieldId}-name-input`}
                            aria-label="Reference name"
                            aria-invalid={Boolean(props.nameError)}
                            aria-describedby={
                                props.nameError ? `${fieldId}-name` : undefined
                            }
                            value={props.name}
                            maxLength={NAME_MAX_LENGTH}
                            readOnly={isSaving}
                            aria-disabled={isSaving ? "true" : undefined}
                            placeholder="Untitled reference"
                            onChange={(event) =>
                                props.onNameChange(event.target.value)
                            }
                            onFocus={() => {
                                sessionStartName.current = props.name;
                            }}
                            onKeyDown={(event) => {
                                if (event.nativeEvent.isComposing) return;
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    if (!isSaving) props.onNameCommit();
                                } else if (event.key === "Escape") {
                                    event.preventDefault();
                                    // A persisted rename restores the name the
                                    // engine captured, not the one this focus
                                    // session happened to start on.
                                    const restored =
                                        props.renameOrigin ||
                                        sessionStartName.current;
                                    if (restored !== "") {
                                        props.onNameCancel(restored);
                                    }
                                }
                            }}
                            onBlur={() => {
                                if (!isSaving) props.onNameCommit();
                            }}
                            className="font-heading hover:bg-muted/50 focus-visible:bg-muted/40 -mx-2 h-auto w-[calc(100%+1rem)] border-0 bg-transparent px-2 py-1 text-2xl font-semibold tracking-tight shadow-none transition-colors md:text-2xl"
                        />
                    )}

                    {props.kind === "reference" && (
                        <span
                            id={`${fieldId}-editor-label`}
                            className="sr-only"
                            aria-hidden="true"
                        >
                            {referenceName || "Untitled reference"}
                        </span>
                    )}

                    {props.kind === "index" && (
                        <div className="mt-4">
                            <label
                                htmlFor={`${fieldId}-when-to-use`}
                                className="text-muted-foreground text-xs font-medium tracking-widest uppercase"
                            >
                                When to use
                            </label>
                            <Textarea
                                id={`${fieldId}-when-to-use`}
                                aria-invalid={Boolean(props.descriptionError)}
                                aria-describedby={`${fieldId}-description`}
                                value={props.description}
                                onChange={(event) =>
                                    props.onDescriptionChange(
                                        event.target.value
                                    )
                                }
                                maxLength={DESCRIPTION_MAX_LENGTH}
                                placeholder="Describe when this skill should activate, e.g. helping brokers prepare renewal reports"
                                className="hover:bg-muted/50 focus-visible:bg-muted/40 -mx-2 mt-1 min-h-0 w-[calc(100%+1rem)] resize-none border-0 bg-transparent px-2 py-1 shadow-none transition-colors"
                            />
                            <p
                                id={`${fieldId}-description`}
                                className={cn(
                                    "mt-1 text-xs",
                                    props.descriptionError
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                )}
                            >
                                {props.descriptionError ??
                                    `${props.description.length}/${DESCRIPTION_MAX_LENGTH}`}
                            </p>
                        </div>
                    )}

                    {props.kind === "reference" && props.nameError && (
                        <p
                            id={`${fieldId}-name`}
                            className="text-destructive mt-2 text-xs"
                        >
                            {props.nameError}
                        </p>
                    )}

                    <Separator className="my-6" />

                    <MarkdownEditor
                        key={`${clientKey}:${resetEpoch}`}
                        ref={editorRef}
                        value={content}
                        onChange={onContentChange}
                        placeholder={
                            props.kind === "index"
                                ? "Write the steps AI Chat should follow..."
                                : "Reference material, examples, or templates for this skill to use..."
                        }
                        minHeight={props.kind === "index" ? 400 : 240}
                        error={Boolean(contentError)}
                        errorId={`${fieldId}-content`}
                        {...(props.kind === "index"
                            ? { labelId: `${fieldId}-title` }
                            : { labelId: `${fieldId}-editor-label` })}
                        suggestions={suggestions}
                        referenceResolution="exact-case"
                        recognitionVocabulary={recognitionVocabulary}
                        onHeadingsChange={onHeadingsChange}
                    />

                    {contentError && (
                        <p
                            id={`${fieldId}-content`}
                            role="alert"
                            className="text-destructive mt-2 text-xs"
                        >
                            {contentError}
                        </p>
                    )}
                </div>
            </div>

            <footer
                data-testid="skill-editor-footer"
                className="bg-background text-muted-foreground sticky bottom-0 shrink-0 border-t text-xs"
            >
                <div
                    data-testid="skill-editor-column"
                    className={cn(
                        CONTENT_COLUMN_CLASS,
                        "flex flex-wrap items-center gap-x-2 gap-y-1 py-2.5"
                    )}
                >
                    <span className="shrink-0">
                        {wordCount.toLocaleString()}{" "}
                        {wordCount === 1 ? "word" : "words"}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className={cn("shrink-0", saveStatus.className)}>
                        {statusLabel}
                    </span>

                    {brokenTargets.map((typedTarget) => (
                        <button
                            key={typedTarget}
                            type="button"
                            onClick={() => onJump?.(typedTarget)}
                            aria-label={`@${typedTarget} unresolved reference`}
                            className={cn(
                                FOOTER_LINK_CLASS,
                                "text-destructive max-w-[16rem] truncate"
                            )}
                        >
                            @{typedTarget}
                        </button>
                    ))}

                    {inboundLinks.map((link) => (
                        <button
                            key={link.clientKey}
                            type="button"
                            onClick={() => onSelectSource?.(link.clientKey)}
                            className={cn(
                                FOOTER_LINK_CLASS,
                                "hover:text-foreground max-w-[16rem] truncate"
                            )}
                        >
                            Referenced by {link.name}
                        </button>
                    ))}

                    <div className="ml-auto flex min-w-0 items-center gap-2">
                        <span className="hidden truncate sm:inline">
                            {props.kind === "index"
                                ? "Mention a reference with @name"
                                : referenceName
                                  ? `Mention this file as @${referenceName}`
                                  : "Name this file to @-mention it"}
                        </span>
                        {props.kind === "reference" && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={props.onDelete}
                                disabled={isSaving}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10 -mr-2 h-7 px-2 text-xs"
                            >
                                Delete reference
                            </Button>
                        )}
                    </div>
                </div>
            </footer>
        </section>
    );
}
